import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectPolicyByNumber } from "../../store/slices/Insurance_Certificate/InsuranceCertificateSlice";
import { ArrowLeft, AlertCircle } from "lucide-react";
import {SendDocumentEmail} from "../../api/sendEmailApi";
import Swal from "sweetalert2";
import {GetPolicyPrintout} from "../../api/printApi";

const InsuranceDetail: React.FC = () => {
  const { policyNumber } = useParams<{ policyNumber: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const currentLanguage = localStorage.getItem('selectedLanguage') || 'az';

  const translations = {
    az: {
      subtitle: "Şəhadətnamə məlumatları aşağıdakı kimidir",
      certificateNumber: "Şəhadətnamə nömrəsi",
      startDate: "Başlama tarixi",
      endDate: "Bitmə tarixi",
      download: "Endir",
      insuranceRules: "Sığorta Qaydaları",
      sendEmail: "E-poçta göndər",
      back: "Geri",
      insuredPerson: "Sığortalı şəxs",
      personType: "Şəxs tipi",
      fin: "FİN",
      insuranceProgram: "Sığorta proqramı",
      policyCertificateNumber: "Sığorta şəhadətnamə nömrəsi",
      insurancePeriod: "Sığorta müddəti",
      insuranceFee: "Sığorta haqqı",
      insuranceAmount: "Sığorta məbləği",
      insuranceSubject: "Sığorta predmeti",
      certificateNotFound: "Şəhadətnamə tapılmadı",
      certificateNotFoundDesc: "Bu nömrəyə aid şəhadətnamə mövcud deyil.",
      goBack: "Geri qayıt",
      rulesModalTitle: "Sığorta Qaydaları",
      emailModalTitle: "E-poçta göndər",
      emailAddress: "E-poçt ünvanı",
      cancel: "Ləğv et",
      send: "Göndər",
      sending: "Göndərilir...",
      error: "Xəta",
      success: "Uğurlu!",
      warning: "Xəbərdarlıq",
      loading: "Yüklənir...",
      pdfPreparing: "PDF hazırlanır",
      certificateNumberNotFound: "Şəhadətnamə nömrəsi tapılmadı",
      errorDownloading: "Şəhadətnamə yüklənərkən xəta baş verdi",
      pdfNotFound: "PDF məlumatı tapılmadı",
      certificateDownloaded: "Şəhadətnamə yükləndi",
      documentNotFound: "Sənəd tapılmadı",
      pleaseEnterEmail: "Zəhmət olmasa e-poçt ünvanını daxil edin",
      pleaseEnterValidEmail: "Zəhmət olmasa düzgün e-poçt ünvanı daxil edin",
      documentSentToEmail: "Sənəd e-poçtunuza göndərildi",
      errorSendingEmail: "E-poçt göndərilərkən xəta baş verdi"
    },
    ru: {
      subtitle: "Информация о полисе следующая",
      certificateNumber: "Номер полиса",
      startDate: "Дата начала",
      endDate: "Дата окончания",
      download: "Скачать",
      insuranceRules: "Правила страхования",
      sendEmail: "Отправить на email",
      back: "Назад",
      insuredPerson: "Застрахованное лицо",
      personType: "Тип лица",
      fin: "ФИН",
      insuranceProgram: "Программа страхования",
      policyCertificateNumber: "Номер страхового полиса",
      insurancePeriod: "Срок страхования",
      insuranceFee: "Страховая премия",
      insuranceAmount: "Страховая сумма",
      insuranceSubject: "Предмет страхования",
      certificateNotFound: "Полис не найден",
      certificateNotFoundDesc: "Полис с этим номером не существует.",
      goBack: "Вернуться назад",
      rulesModalTitle: "Правила страхования",
      emailModalTitle: "Отправить на email",
      emailAddress: "Адрес электронной почты",
      cancel: "Отмена",
      send: "Отправить",
      sending: "Отправка...",
      error: "Ошибка",
      success: "Успешно!",
      warning: "Предупреждение",
      loading: "Загрузка...",
      pdfPreparing: "Подготовка PDF",
      certificateNumberNotFound: "Номер полиса не найден",
      errorDownloading: "Ошибка при загрузке полиса",
      pdfNotFound: "PDF данные не найдены",
      certificateDownloaded: "Полис загружен",
      documentNotFound: "Документ не найден",
      pleaseEnterEmail: "Пожалуйста, введите адрес электронной почты",
      pleaseEnterValidEmail: "Пожалуйста, введите правильный адрес электронной почты",
      documentSentToEmail: "Документ отправлен на вашу почту",
      errorSendingEmail: "Ошибка при отправке email"
    },
    en: {
      subtitle: "Certificate information is as follows",
      certificateNumber: "Certificate number",
      startDate: "Start date",
      endDate: "End date",
      download: "Download",
      insuranceRules: "Insurance Rules",
      sendEmail: "Send to email",
      back: "Back",
      insuredPerson: "Insured person",
      personType: "Person type",
      fin: "FIN",
      insuranceProgram: "Insurance program",
      policyCertificateNumber: "Insurance certificate number",
      insurancePeriod: "Insurance period",
      insuranceFee: "Insurance premium",
      insuranceAmount: "Insurance amount",
      insuranceSubject: "Insurance subject",
      certificateNotFound: "Certificate not found",
      certificateNotFoundDesc: "Certificate with this number does not exist.",
      goBack: "Go back",
      rulesModalTitle: "Insurance Rules",
      emailModalTitle: "Send to email",
      emailAddress: "Email address",
      cancel: "Cancel",
      send: "Send",
      sending: "Sending...",
      error: "Error",
      success: "Success!",
      warning: "Warning",
      loading: "Loading...",
      pdfPreparing: "Preparing PDF",
      certificateNumberNotFound: "Certificate number not found",
      errorDownloading: "Error downloading certificate",
      pdfNotFound: "PDF data not found",
      certificateDownloaded: "Certificate downloaded",
      documentNotFound: "Document not found",
      pleaseEnterEmail: "Please enter email address",
      pleaseEnterValidEmail: "Please enter a valid email address",
      documentSentToEmail: "Document sent to your email",
      errorSendingEmail: "Error sending email"
    }
  };

  const t = translations[currentLanguage as keyof typeof translations];

  const policyFromRedux = useSelector((state: any) =>
    selectPolicyByNumber(state, policyNumber || "")
  )

  const [policy, setPolicy] = useState<any>(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const loadPolicy = () => {
      if (policyFromRedux) {
        setPolicy(policyFromRedux);
        localStorage.setItem('currentPolicyDetail', JSON.stringify(policyFromRedux));
        setIsInitialLoading(false);
        return;
      }

      if (location.state?.policy) {
        setPolicy(location.state.policy);
        localStorage.setItem('currentPolicyDetail', JSON.stringify(location.state.policy));
        setIsInitialLoading(false);
        return;
      }

      const savedPolicy = localStorage.getItem('currentPolicyDetail');
      if (savedPolicy) {
        try {
          const parsedPolicy = JSON.parse(savedPolicy);
          if (parsedPolicy.policy_number === policyNumber) {
            setPolicy(parsedPolicy);
          }
        } catch (error) {
          console.error('Error parsing policy from localStorage:', error);
          localStorage.removeItem('currentPolicyDetail');
        }
      }

      setIsInitialLoading(false);
    };

    loadPolicy();
  }, [policyFromRedux, policyNumber, location.state]);

  useEffect(() => {
    return () => {
      setTimeout(() => {
        const newPath = window.location.pathname;
        if (!newPath.includes('/insurance-detail/')) {
          localStorage.removeItem('currentPolicyDetail');
        }
      }, 0);
    };
  }, []);
  const handleDownload = async () => {
    if (!policy?.policy_number) {
      Swal.fire({
        icon: 'error',
        title: t.error,
        text: t.certificateNumberNotFound,
        confirmButtonColor: '#d33',
      });
      return;
    }

    try {
      Swal.fire({
        title: t.loading,
        html: '<div class="flex flex-col items-center gap-3"><div class="animate-spin rounded-full h-12 w-12 border-b-2 border-colorpink"></div><p class="text-gray-600">' + t.pdfPreparing + '</p></div>',
        allowOutsideClick: false,
        showConfirmButton: false,
      });

      const result = await GetPolicyPrintout(policy.policy_number);

      if (result.status !== 200) {
        let errorMessage = t.errorDownloading;
        if (result.status === 404) {
          errorMessage = t.certificateNotFound;
        } else if ((result.data as any).error) {
          errorMessage = (result.data as any).error;
        }
        Swal.fire({
          icon: 'error',
          title: t.error,
          text: errorMessage,
          confirmButtonColor: '#d33',
        });
        return;
      }

      const responseData = result.data as any;

      if (!responseData.pdf_base64) {
        Swal.fire({
          icon: 'error',
          title: t.error,
          text: t.pdfNotFound,
          confirmButtonColor: '#d33',
        });
        return;
      }

      Swal.close();

      const base64Data = responseData.pdf_base64;
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);

      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/octet-stream' });

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Sehadetname_${policy.policy_number}.pdf`;

      a.setAttribute('target', '_blank');
      a.style.display = 'none';

      document.body.appendChild(a);

      const clickEvent = new MouseEvent('click', {
        view: window,
        bubbles: true,
        cancelable: true,
        buttons: 1
      });

      a.dispatchEvent(clickEvent);

      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);


      }, 300);

    } catch (error) {
      console.error('Download error:', error);
      Swal.fire({
        icon: 'error',
        title: t.error,
        text: t.errorDownloading,
        confirmButtonColor: '#d33',
      });
    }
  };  const handleDocumentOpen = () => {
    if (policy?.product_document) {
      const doc = policy.product_document;

      if (doc.startsWith('http://') || doc.startsWith('https://')) {
        window.open(doc, '_blank');
        return;
      }

      let pdfData = '';
      if (doc.startsWith('data:application/pdf;base64,')) {
        pdfData = doc;
      } else {
        pdfData = `data:application/pdf;base64,${doc}`;
      }

      try {
        const byteCharacters = atob(pdfData.split(',')[1]);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        const blobUrl = URL.createObjectURL(blob);

        window.open(blobUrl, '_blank');

        setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
      } catch (error) {
        console.error('PDF açılmadı:', error);
        Swal.fire({
          icon: 'error',
          title: t.error,
          text: 'PDF açıla bilmədi. Zəhmət olmasa yenidən cəhd edin.',
          confirmButtonColor: '#d33',
        });
      }
    } else {
      Swal.fire({
        icon: 'error',
        title: t.error,
        text: t.documentNotFound,
        confirmButtonColor: '#d33',
      });
    }
  };

  const handleSendEmail = () => {
    setShowEmailModal(true);
    setEmail("");
  };

  const formatDate = (dateString: string): string => {
    return dateString.replace(/-/g, '.');
  };

  const handleEmailSubmit = async () => {
    if (!email || !email.trim()) {
      Swal.fire({
        icon: 'warning',
        title: t.warning,
        text: t.pleaseEnterEmail,
        confirmButtonColor: '#d33',
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      Swal.fire({
        icon: 'error',
        title: t.error,
        text: t.pleaseEnterValidEmail,
        confirmButtonColor: '#d33',
      });
      return;
    }

    if (!policy?.policy_number) {
      Swal.fire({
        icon: 'error',
        title: t.error,
        text: t.certificateNumberNotFound,
        confirmButtonColor: '#d33',
      });
      return;
    }

    setIsLoading(true);
    try {
      const response = await SendDocumentEmail({
        email: email,
        policy_number: policy.policy_number
      });

      if (response.status === 200) {
        setShowEmailModal(false);
        setEmail("");

        Swal.fire({
          icon: 'success',
          title: t.success,
          text: t.documentSentToEmail,
          confirmButtonColor: '#10b981',
          timer: 3000
        });
      } else {
        let errorMessage = t.errorSendingEmail;

        if (response.status === 404) {
          errorMessage = t.certificateNotFound;
        } else if ('message' in response.data) {
          errorMessage = response.data.message;
        } else if ('error' in response.data) {
          errorMessage = response.data.error || errorMessage;
        }

        Swal.fire({
          icon: 'error',
          title: t.error,
          text: errorMessage,
          confirmButtonColor: '#d33',
        });
      }
    } catch (error: any) {
      console.error('Error sending email:', error);

      let errorMessage = t.errorSendingEmail;

      if (error.response) {
        if (error.response.status === 404) {
          errorMessage = t.certificateNotFound;
        } else if (error.response.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data?.error) {
          errorMessage = error.response.data.error;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      Swal.fire({
        icon: 'error',
        title: t.error,
        text: errorMessage,
        confirmButtonColor: '#d33',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Yükləmə göstəricisi
  if (isInitialLoading) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-colorpink mx-auto mb-4"></div>
          <p className="text-gray-600">{t.loading}</p>
        </div>
      </div>
    );
  }

  // Policy tapılmadı
  if (!policy) {
    return (
      <div className="min-h-screen">
        <div className="mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-xl p-6">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-6 h-6 text-red-600" />
              <div>
                <h3 className="font-semibold text-red-900">
                  {t.certificateNotFound}
                </h3>
                <p className="text-sm text-red-700 mt-1">
                  {t.certificateNotFoundDesc}
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate(-1)}
              className="mt-4 flex items-center gap-2 text-red-700 hover:text-red-900"
            >
              <ArrowLeft className="w-4 h-4" />
              {t.goBack}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const hasDocument = policy.product_document;
  const hasPrintout = policy.has_printout;
  const showAnyButton = hasDocument || hasPrintout;

  return (
    <div className="min-h-screen">
      {/* Email Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">{t.emailModalTitle}</h3>
              <button
                onClick={() => {
                  setShowEmailModal(false);
                  setEmail("");
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                disabled={isLoading}
              >
                ✕
              </button>
            </div>
            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t.emailAddress}
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="example@gmail.com"
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-colorpink focus:border-transparent focus:outline-none transition-all"
                disabled={isLoading}
              />
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowEmailModal(false);
                    setEmail("");
                  }}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                  disabled={isLoading}
                >
                  {t.cancel}
                </button>
                <button
                  onClick={handleEmailSubmit}
                  className="flex-1 px-4 py-3 bg-colorpink text-white rounded-lg hover:bg-pink-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>{t.sending}</span>
                    </>
                  ) : (
                    t.send
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="">
        {/* Header */}
        <div className="mb-6 lg:mb-8">
          <h1 className="text-3xl font-semibold text-stone-800">
            {policy.policy_type}
          </h1>
          <p className="text-stone-600 text-base mb-6">
            {t.subtitle}
          </p>
        </div>

        <div className="rounded-2xl w-full">
          <div className="flex flex-col lg:flex-row lg:justify-between gap-6">
            {/* Left Card Section */}
            <div className="w-full lg:w-auto lg:flex-shrink-0">
              <div
                className="rounded-xl p-6 text-white shadow-lg h-auto lg:h-96 w-full lg:w-72 flex flex-col justify-between"
                style={{ backgroundColor: policy.product_color ? policy.product_color : "#94005b" }}
              >
                <div className="flex justify-between items-start">
                  <div className="w-full">
                    <p className="text-sm sm:text-base">{t.certificateNumber}</p>
                    <div className="flex items-center gap-3 pt-1">
                      <span className="text-xl font-semibold break-all">
                        {policy.policy_number}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-4 sm:gap-6 mt-6 lg:mt-0">
                  <div>
                    <p className="text-xs sm:text-sm opacity-90 mb-1">
                      {t.startDate}
                    </p>
                    <p className="text-base sm:text-lg font-semibold">
                      {formatDate(policy.start_date)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm opacity-90 mb-1">
                      {t.endDate}
                    </p>
                    <p className="text-base sm:text-lg font-semibold">
                      {formatDate(policy.end_date)}
                    </p>
                  </div>
                </div>
              </div>

              {showAnyButton && (
                <div className="space-y-2 sm:space-y-4 mt-4 sm:mt-8">
                  {hasPrintout && (
                    <button
                      onClick={handleDownload}
                      className="w-full lg:w-72 bg-gray-200 border border-gray-400 hover:bg-colorpink hover:border-pink-800 hover:text-white text-gray-800 py-3 px-4 rounded-xl font-medium flex items-center gap-2 text-sm sm:text-base transition-colors"
                    >
                      <span className="w-8 text-center">
                        <i className="fas fa-download text-base sm:text-lg mr-2"></i>
                      </span>
                      <span>{t.download}</span>
                    </button>
                  )}
                  {hasDocument && (
                    <button
                      onClick={handleDocumentOpen}
                      className="w-full lg:w-72 bg-gray-200 border border-gray-400 hover:bg-colorpink hover:border-pink-800 hover:text-white text-gray-800 py-3 px-4 rounded-xl font-medium flex items-center gap-2 text-sm sm:text-base transition-colors"
                    >
                      <span className="w-8 text-center">
                        <i className="fas fa-book text-base sm:text-lg mr-2"></i>
                      </span>
                      <span>{t.insuranceRules}</span>
                    </button>
                  )}
                  {hasPrintout && (
                    <button
                      onClick={handleSendEmail}
                      className="w-full lg:w-72 bg-gray-200 border border-gray-400 hover:bg-colorpink hover:border-pink-800 hover:text-white text-gray-800 py-3 px-4 rounded-xl font-medium flex items-center gap-2 text-sm sm:text-base transition-colors"
                    >
                      <span className="w-8 text-center">
                        <i className="fas fa-envelope text-base sm:text-lg"></i>
                      </span>
                      <span>{t.sendEmail}</span>
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Right Details Section */}
            <div className="flex-1 p-4 sm:p-6 bg-white rounded-xl border border-gray-200">
              <div className="space-y-4 sm:space-y-6">
                {/* Sığortalı şəxs */}
                <div>
                  <h3 className="text-xs sm:text-sm text-gray-600 font-medium mb-2">
                    {t.insuredPerson}
                  </h3>
                  <div className="flex items-center gap-2">
                    <span className="text-sm sm:text-base text-gray-800 font-medium">
                      {policy.insured_person_name}
                    </span>
                  </div>
                </div>

                {/* Şəxs tipi */}
                <div>
                  <h3 className="text-xs sm:text-sm text-gray-600 font-medium mb-2">
                    {t.personType}
                  </h3>
                  <p className="text-sm sm:text-base text-gray-800 font-medium">
                    {policy.insured_person_type}
                  </p>
                </div>

                {/* FİN/VÖEN */}
                <div>
                  <h3 className="text-xs sm:text-sm text-gray-600 font-medium mb-1">
                    {t.fin}
                  </h3>
                  <p className="text-sm sm:text-base text-gray-800">
                    {policy.insured_person_pin_or_tin}
                  </p>
                </div>

                {/* Sığorta proqramı */}
                <div>
                  <h3 className="text-xs sm:text-sm text-gray-600 font-medium mb-2">
                    {t.insuranceProgram}
                  </h3>
                  <p className="text-sm sm:text-base text-gray-800">
                    {policy.policy_type}
                  </p>
                </div>

                {/* Şəhadətnamə nömrəsi */}
                <div>
                  <h3 className="text-xs sm:text-sm text-gray-600 font-medium mb-2">
                    {t.policyCertificateNumber}
                  </h3>
                  <p className="text-sm sm:text-base text-gray-800 font-mono break-all">
                    {policy.policy_number}
                  </p>
                </div>

                {/* Sığorta müddəti və Status */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                  <div>
                    <h3 className="text-xs sm:text-sm text-gray-600 font-medium mb-1">
                      {t.insurancePeriod}
                    </h3>
                    <p className="text-sm sm:text-base text-gray-800">
                      {formatDate(policy.start_date)} - {formatDate(policy.end_date)}
                    </p>
                  </div>
                  <div className="bg-green-700 text-white px-3 py-1 rounded-full text-xs sm:text-sm w-fit font-medium">
                    {policy.status}
                  </div>
                </div>

                {/* Maliyyə məlumatları */}
                <div className="pt-4 border-t border-gray-200">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <h3 className="text-xs sm:text-sm text-gray-600 font-medium mb-1">
                        {t.insuranceFee}
                      </h3>
                      <p className="text-sm sm:text-base text-gray-800 font-semibold">
                        {policy.insurance_amount} {policy.currency}
                      </p>
                    </div>
                    <div>
                      <h3 className="text-xs sm:text-sm text-gray-600 font-medium mb-1">
                        {t.insuranceAmount}
                      </h3>
                      <p className="text-sm sm:text-base text-gray-800 font-semibold">
                        {policy.premium} {policy.currency}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Sığorta predmeti */}
                <div className="pt-4 border-t border-gray-200">
                  <h3 className="text-xs sm:text-sm text-gray-600 font-medium mb-3">
                    {t.insuranceSubject}
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-700">
                    {policy.insurance_subject}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Back Button */}
        <div className="mt-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 px-4 py-1 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-colorpink hover:text-white hover:border-gray-400 transition-all duration-200 shadow-sm hover:shadow font-medium"
          >
            <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="text-sm sm:text-base">{t.back}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default InsuranceDetail;